import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { StateManagementService, BillingSetup } from '../../services/state-management.service';
import { OnboardingApiService } from '../../services/onboarding-api.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-billing-setup',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDividerModule,
    MatCardModule,
    MatChipsModule,
  ],
  templateUrl: './billing-setup.component.html',
  styleUrl: './billing-setup.component.scss',
})
export class BillingSetupComponent implements OnInit {
  @Input() organizationId!: string;
  @Output() stepComplete = new EventEmitter<BillingSetup>();

  isSettingUp = false;
  isConfirming = false;
  billingActive = false;
  bankName = '';
  bankLast4 = '';

  private stripe: Stripe | null = null;

  constructor(
    public stateService: StateManagementService,
    private api: OnboardingApiService,
    private snackBar: MatSnackBar,
  ) {}

  async ngOnInit(): Promise<void> {
    // Pre-load Stripe.js
    this.stripe = await loadStripe(environment.stripeKey);

    // Subscribe to state changes to update billing status
    this.stateService.state$.subscribe(state => {
      this.billingActive = state.billingActive;
      if (this.billingActive) {
        this.bankName = state.billingSetup?.bankName || '';
        this.bankLast4 = state.billingSetup?.bankLast4 || '';
      }
    });

    // Pre-populate if billing was already set up (reload / Stripe return)
    const currentState = this.stateService.snapshot;
    if (currentState.billingActive) {
      this.billingActive = true;
      this.bankName = currentState.billingSetup?.bankName || '';
      this.bankLast4 = currentState.billingSetup?.bankLast4 || '';
    }
  }

  async linkBankAccount(): Promise<void> {
    if (!this.stripe) {
      this.snackBar.open('Stripe is not loaded yet. Please try again.', 'Close', { duration: 4000 });
      return;
    }

    this.isSettingUp = true;
    try {
      // Step 1: Create SetupIntent on the backend
      const setupResult = await this.api.setupOrgBilling(this.organizationId).toPromise();
      const clientSecret = setupResult?.clientSecret;
      const setupIntentId = setupResult?.setupIntentId;

      if (!clientSecret || !setupIntentId) {
        throw new Error('Invalid response from billing setup.');
      }

      const org = this.stateService.snapshot.organization;
      const billingName = org?.Name || 'Organization';
      const billingEmail = org?.Email || '';

      // Step 2: Open Stripe Financial Connections to collect bank account
      const collectResult = await (this.stripe as any).collectBankAccountForSetup({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: billingName,
              email: billingEmail,
            },
          },
        },
        expand: ['payment_method'],
      });

      if (collectResult.error) {
        throw new Error(collectResult.error.message || 'Bank account collection failed.');
      }

      const setupIntent = collectResult.setupIntent;
      if (!setupIntent) {
        throw new Error('No SetupIntent returned from Stripe.');
      }

      // Step 3: If bank was collected but needs confirmation, confirm it
      if (setupIntent.status === 'requires_confirmation') {
        const confirmResult = await (this.stripe as any).confirmUsBankAccountSetup(clientSecret);
        if (confirmResult.error) {
          throw new Error(confirmResult.error.message || 'Bank account confirmation failed.');
        }
      }

      // Step 4: Tell our backend to finalize and save the payment method
      this.isSettingUp = false;
      this.isConfirming = true;

      const confirmResult = await this.api.confirmOrgBillingAccount(
        this.organizationId,
        setupIntentId,
      ).toPromise();

      this.bankName = confirmResult?.bankName || 'Bank';
      this.bankLast4 = confirmResult?.bankLast4 || '****';
      this.billingActive = true;

      const setup: BillingSetup = {
        bankName: this.bankName,
        bankLast4: this.bankLast4,
        customerId: confirmResult?.customerId || '',
      };
      this.stateService.setBillingActive(setup);

      this.snackBar.open('Bank account linked successfully!', 'Close', { duration: 3000 });
      this.stepComplete.emit(setup);
    } catch (err: any) {
      this.snackBar.open('Error: ' + (err.message || err), 'Close', { duration: 6000 });
    } finally {
      this.isSettingUp = false;
      this.isConfirming = false;
    }
  }

  onSkip(): void {
    this.snackBar.open('Billing setup skipped. You can complete this later.', 'Close', { duration: 4000 });
    this.stepComplete.emit({ bankName: '', bankLast4: '', customerId: '' });
  }

  onContinue(): void {
    const setup: BillingSetup = {
      bankName: this.bankName,
      bankLast4: this.bankLast4,
      customerId: this.stateService.snapshot.billingSetup?.customerId || '',
    };
    this.stepComplete.emit(setup);
  }
}
